import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { LanguageToggle } from "../components/LanguageToggle";
import { Button } from "../components/ui/button";
import {
	SectionHeader,
	useLandingDocumentScroll,
	useMetricoolTracker,
	useRevealOnScroll,
} from "../components/landing/landingShared";
import {
	Activity,
	ArrowRight,
	BarChart3,
	CalendarCheck,
	Check,
	Dumbbell,
	Layers,
	Minus,
	Plus,
	Scale,
	Sparkles,
	Timer,
	Trophy,
	Users,
} from "lucide-react";

/**
 * Pagina informativa pubblica (rotta "/info").
 * Destinazione dei risponditori automatici e delle campagne social: chi scrive
 * in privato riceve questo link per avere informazioni piu' dettagliate.
 * Contenuti dalla presentazione «KRAVEON – Presentazione INFO» (nov 2026).
 * Nessun modulo: i contatti arrivano in privato sui social.
 * Tutti i numeri sono dichiarati come esempio, come nella presentazione.
 */

const COPY = {
	it: {
		login: "Accedi",
		app: "Vai all'app",
		example: "Esempio",
		exampleData: "Dati di esempio",
		hero: {
			kicker: "Piattaforma di coaching per coach, preparatori e box",
			h1a: "Meno tempo a gestire.",
			h1b: "Più tempo per allenare.",
			sub: "Programmi, atleti, risultati e feedback in un unico ambiente di lavoro. L'AI che lavora dentro i tuoi programmi.",
			cta2: "Scopri Kraveon",
			meso: {
				label: "Mesociclo · 4 sett.",
				title: "Forza e tecnica",
				exercise: "Esercizio",
				weeks: ["S1", "S2", "S3", "S4"],
				rows: [
					["Back Squat", "75%", "80%", "85%", "65%"],
					["Front Squat", "70%", "74%", "78%", "60%"],
					["Complex", "65%", "70%", "75%", "60%"],
				],
				note: "S4 = settimana di scarico",
			},
		},
		problem: {
			kicker: "Il problema",
			title: "Il lavoro del coach è molto più che scrivere schede",
			items: [
				{
					k: "Il programma",
					v: "Ore su fogli di calcolo e copia-incolla tra versioni che non coincidono più.",
				},
				{
					k: "Gli atleti",
					v: "Risultati sparsi tra chat, file e screenshot: manca una visione d'insieme.",
				},
				{
					k: "La crescita",
					v: "Più atleti significa più caos: il tempo del coach diventa il vero limite.",
				},
			],
			chaos: "Excel, chat, PDF, screenshot, calendario gare, appunti…",
		},
		solution: {
			kicker: "La soluzione",
			title: "Un unico flusso di lavoro, dalla pianificazione alla nuova programmazione",
			steps: [
				{
					t: "Pianifica",
					d: "Builder a settimane, giorni e sedute. Vista a mesocicli.",
					who: "Coach",
				},
				{
					t: "Assegna",
					d: "Programmi ad atleti e gruppi. Pubblichi quando sei pronto.",
					who: "Coach",
				},
				{
					t: "Allena",
					d: "L'atleta vede la seduta, i video e i carichi già calcolati.",
					who: "Atleta",
				},
				{
					t: "Registra",
					d: "Serie, RPE, note e diario benessere dall'app.",
					who: "Atleta",
				},
				{
					t: "Analizza",
					d: "Feedback, record personali, profilo prestativo, monitoraggio (beta).",
					who: "Coach + AI",
					ai: true,
				},
				{
					t: "Riparti",
					d: "Il blocco successivo, anche in bozza con l'AI collegata.",
					who: "Coach + AI",
					ai: true,
				},
			],
		},
		why: {
			kicker: "Perché Kraveon",
			title: "Sei cose che fanno la differenza",
			items: [
				{
					t: "AI nei tuoi programmi",
					d: "L'AI legge i tuoi dati e scrive bozze direttamente in Kraveon. Pubblichi sempre tu.",
					hl: true,
				},
				{
					t: "Screening delle carenze",
					d: "Livello su 10 per ogni esercizio, kg al livello successivo e radar per dominio.",
					hl: true,
				},
				{
					t: "Categorie di peso",
					d: "Leggi gli stessi massimali su due categorie e scegli con i numeri.",
					hl: true,
				},
				{
					t: "Programmato / eseguito",
					d: "In beta: chi si è allenato, quando e con quali volumi. Un dato mancante non diventa zero.",
				},
				{
					t: "Strutture native",
					d: "Complex, superset, EMOM, AMRAP, HIIT, corsa, bici, nuoto, erg: non un campo note.",
				},
				{
					t: "Da coach, per coach",
					d: "Nato in palestra, non in ufficio: evolve sulle richieste che arrivano dal campo.",
				},
			],
		},
		builder: {
			kicker: "Programmazione",
			title: "Un builder nato per programmare davvero",
			points: [
				{
					t: "Settimane, giorni e sedute multiple",
					d: "Anche doppia seduta nella stessa giornata",
				},
				{
					t: "Percentuali sul massimale",
					d: "L'app le converte in chilogrammi per ogni atleta",
				},
				{
					t: "Serie, ripetizioni, RPE, RIR e recuperi",
					d: "Riga per riga, con il video di spiegazione",
				},
				{
					t: "Riscaldamento, defaticamento e note",
					d: "Per giorno e per esercizio",
				},
				{
					t: "Pubblicazione per settimana",
					d: "Gli atleti vedono solo ciò che hai pubblicato: scegli tu quando",
				},
			],
			phone: {
				label: "Vista atleta",
				title: "Seduta del giorno",
				items: [
					{ n: "Back Squat", p: "4 × 5 @ 75%" },
					{ n: "Front Squat", p: "3 × 3 @ 70%" },
				],
				loadNote: "Carico calcolato dal massimale",
				foot: "Registri serie, RPE e note dal telefono.",
			},
		},
		training: {
			kicker: "Allenamenti",
			title: "Forza, conditioning, endurance: strutture native, non un campo note",
			groups: [
				{
					t: "Forza",
					items: [
						"Serie e ripetizioni",
						"Complex sul bilanciere",
						"Superset",
						"EMOM di forza",
					],
				},
				{
					t: "Conditioning",
					items: ["EMOM", "AMRAP", "For Time", "HIIT"],
				},
				{
					t: "Endurance",
					items: ["Corsa", "Bici", "Nuoto", "Erg (ergometri)"],
				},
				{
					t: "Benchmark",
					items: [
						"Test di riferimento",
						"Classifiche di gruppo",
						"Calendario gare",
					],
				},
			],
			foot: "Round, tempi e punteggi li registra l'atleta: il coach li ritrova nello storico.",
		},
		ai: {
			kicker: "Kraveon e l'intelligenza artificiale",
			title: "Un'AI che lavora con i tuoi dati",
			generic: {
				t: "Chatbot generico",
				items: [
					"Non conosce i tuoi atleti",
					"Devi incollare i dati a mano",
					"Restituisce testo da ricopiare nella scheda",
					"Ogni volta riparti da zero",
				],
			},
			linked: {
				t: "AI collegata a Kraveon",
				items: [
					"Legge atleti, risultati, feedback e gare",
					"Usa gli esercizi reali della tua libreria",
					"Scrive il programma direttamente nell'app",
					"Lascia una bozza non pubblicata da approvare",
				],
			},
			demo: {
				t: "L'AI prepara. Il coach decide.",
				coach: "Coach",
				ask: "«Aggiungi all'atleta demo una settimana di scarico dopo la 4 e porta gli squat al 65%.»",
				reply: "AI + Kraveon",
				answer: "Settimana 5 creata in bozza, non ancora pubblicata agli atleti. Punto di ripristino salvato.",
			},
			foot: "Funziona con l'assistente integrato e con il connettore per Claude e ChatGPT.",
		},
		models: {
			kicker: "Modelli prestativi",
			title: "Le carenze di ogni atleta, a colpo d'occhio",
			points: [
				{
					t: "Sport e modelli",
					d: "Usi il modello di sistema o crei il tuo, da zero.",
				},
				{
					t: "Livello su 10 per ogni esercizio",
					d: "Con i kg che mancano al livello successivo.",
				},
				{
					t: "Radar per dominio",
					d: "Punti di forza e aree da sviluppare: prima di scrivere il blocco successivo sai su cosa lavorare.",
				},
			],
			card: {
				label: "Weightlifting · −85 kg",
				rows: [
					{ n: "Back squat", m: "160 kg · Agonista", lvl: 4, gap: "+27 kg" },
					{ n: "Strappo", m: "115 kg · Agonista naz.", lvl: 5, gap: "+15 kg" },
					{ n: "Slancio", m: "135 kg · Agonista", lvl: 4, gap: "+4 kg" },
				],
				levelOf: "Livello",
				toNext: "al successivo",
				note: "Livello e kg al successivo secondo il modello di sistema Weightlifting.",
			},
			foot: "Valutazione sportiva della prestazione: i livelli sono una lettura indicativa dei massimali.",
		},
		weight: {
			kicker: "Categorie di peso",
			title: "−85 o −75? Scegli con i numeri",
			from: "Categoria −85 kg",
			to: "Categoria −75 kg · Confronta con",
			rows: [
				{ n: "Strappo · 110 kg", a: 4, b: 5 },
				{ n: "Slancio · 135 kg", a: 4, b: 5 },
				{ n: "Back squat · 175 kg", a: 4, b: 5 },
			],
			qLabel: "La domanda",
			q: "In quale categoria conviene far gareggiare l'atleta? Spesso si decide a sensazione.",
			rLabel: "Il risultato",
			r: "Con «Confronta con» leggi gli stessi massimali sulle due tabelle, con il valore Sinclair.",
			foot: "Dati di esempio · soglie del modello di sistema Weightlifting.",
		},
		progression: {
			kicker: "Progressioni",
			title: "Progressione a colpo d'occhio",
			label: "Mesociclo · 4 settimane · Atleta demo",
			exercise: "Esercizio",
			week: "Settimana",
			rows: [
				["Back Squat", "4 × 5 @ 70%", "4 × 5 @ 75%", "5 × 5 @ 75%", "5 × 5 @ 80%"],
				["Front Squat", "3 × 5 @ 65%", "3 × 5 @ 70%", "4 × 5 @ 70%", "4 × 5 @ 75%"],
				["Strappo", "5 × 2 @ 70%", "6 × 2 @ 70%", "6 × 2 @ 75%", "7 × 2 @ 75%"],
				["Stacco", "3 × 5 @ 70%", "4 × 5 @ 75%", "4 × 5 @ 75%", "5 × 5 @ 80%"],
			],
			volume: "Volume della settimana",
			volumeSub: "Ripetizioni totali",
			volumes: [60, 67, 77, 84],
			foot: "Carichi in percentuale sul massimale: l'app calcola i kg di ogni atleta e ne misura l'andamento nel tempo.",
		},
		audience: {
			kicker: "Per chi è",
			title: "Dal singolo atleta al box con più coach",
			items: [
				{
					t: "Personal trainer",
					items: [
						"Programmi individuali",
						"Carichi sul massimale",
						"Diario e note dell'atleta",
					],
				},
				{
					t: "Coach online",
					items: [
						"L'atleta si allena ovunque dalla web app",
						"Feedback nel contesto della seduta",
					],
				},
				{
					t: "Cross e functional training",
					items: [
						"EMOM, AMRAP, For Time e HIIT nativi",
						"Benchmark e classifiche di gruppo",
						"Calendario gare",
					],
				},
				{
					t: "Box e palestre",
					items: [
						"Programmazione per gruppi",
						"Un atleta in più gruppi",
						"Community con le classifiche",
					],
				},
				{
					t: "Staff con più coach",
					items: [
						"Più coach sullo stesso atleta",
						"Ogni coach vede i suoi atleti",
						"Dati separati tra coach",
					],
				},
			],
			pills: [
				"Web app installabile",
				"Italiano e inglese",
				"Tema chiaro e scuro",
				"Atleti su invito del coach",
			],
		},
		final: {
			lines: [
				"Tu decidi il programma.",
				"Kraveon lo organizza.",
				"L'AI ti aiuta a prepararlo.",
			],
			tagline: "Da coach per coach.",
			cta: "Scopri Kraveon",
		},
		footer: { privacy: "Privacy", cookie: "Cookie" },
	},
	en: {
		login: "Log in",
		app: "Go to the app",
		example: "Example",
		exampleData: "Sample data",
		hero: {
			kicker: "Coaching platform for coaches, trainers and boxes",
			h1a: "Less time managing.",
			h1b: "More time coaching.",
			sub: "Programs, athletes, results and feedback in a single workspace. AI that works inside your programs.",
			cta2: "Discover Kraveon",
			meso: {
				label: "Mesocycle · 4 wks",
				title: "Strength & technique",
				exercise: "Exercise",
				weeks: ["W1", "W2", "W3", "W4"],
				rows: [
					["Back Squat", "75%", "80%", "85%", "65%"],
					["Front Squat", "70%", "74%", "78%", "60%"],
					["Complex", "65%", "70%", "75%", "60%"],
				],
				note: "W4 = deload week",
			},
		},
		problem: {
			kicker: "The problem",
			title: "Coaching is much more than writing workouts",
			items: [
				{
					k: "The program",
					v: "Hours on spreadsheets and copy-pasting between versions that no longer match.",
				},
				{
					k: "The athletes",
					v: "Results scattered across chats, files and screenshots: no overall picture.",
				},
				{
					k: "Growth",
					v: "More athletes means more chaos: the coach's time becomes the real limit.",
				},
			],
			chaos: "Excel, chats, PDFs, screenshots, competition calendar, notes…",
		},
		solution: {
			kicker: "The solution",
			title: "One workflow, from planning to the next block",
			steps: [
				{
					t: "Plan",
					d: "Builder by weeks, days and sessions. Mesocycle view.",
					who: "Coach",
				},
				{
					t: "Assign",
					d: "Programs to athletes and groups. Publish when you're ready.",
					who: "Coach",
				},
				{
					t: "Train",
					d: "The athlete sees the session, videos and pre-calculated loads.",
					who: "Athlete",
				},
				{
					t: "Log",
					d: "Sets, RPE, notes and wellness diary from the app.",
					who: "Athlete",
				},
				{
					t: "Analyse",
					d: "Feedback, personal records, performance profile, monitoring (beta).",
					who: "Coach + AI",
					ai: true,
				},
				{
					t: "Restart",
					d: "The next block, even as a draft with the connected AI.",
					who: "Coach + AI",
					ai: true,
				},
			],
		},
		why: {
			kicker: "Why Kraveon",
			title: "Six things that make the difference",
			items: [
				{
					t: "AI in your programs",
					d: "The AI reads your data and writes drafts straight into Kraveon. You always publish.",
					hl: true,
				},
				{
					t: "Gap screening",
					d: "A level out of 10 for each exercise, kg to the next level and a radar per domain.",
					hl: true,
				},
				{
					t: "Weight classes",
					d: "Read the same maxes against two classes and choose with the numbers.",
					hl: true,
				},
				{
					t: "Planned / done",
					d: "In beta: who trained, when and with what volume. Missing data never becomes zero.",
				},
				{
					t: "Native structures",
					d: "Complex, superset, EMOM, AMRAP, HIIT, running, bike, swim, erg: not a notes field.",
				},
				{
					t: "By coaches, for coaches",
					d: "Born in the gym, not in an office: it evolves on requests from the field.",
				},
			],
		},
		builder: {
			kicker: "Programming",
			title: "A builder made for real programming",
			points: [
				{
					t: "Weeks, days and multiple sessions",
					d: "Even two sessions on the same day",
				},
				{
					t: "Percentages of the max",
					d: "The app converts them into kilograms for each athlete",
				},
				{
					t: "Sets, reps, RPE, RIR and rest",
					d: "Row by row, with the demo video",
				},
				{
					t: "Warm-up, cool-down and notes",
					d: "Per day and per exercise",
				},
				{
					t: "Publishing by week",
					d: "Athletes only see what you've published: you choose when",
				},
			],
			phone: {
				label: "Athlete view",
				title: "Today's session",
				items: [
					{ n: "Back Squat", p: "4 × 5 @ 75%" },
					{ n: "Front Squat", p: "3 × 3 @ 70%" },
				],
				loadNote: "Load calculated from the max",
				foot: "Log sets, RPE and notes from the phone.",
			},
		},
		training: {
			kicker: "Training",
			title: "Strength, conditioning, endurance: native structures, not a notes field",
			groups: [
				{
					t: "Strength",
					items: [
						"Sets and reps",
						"Barbell complexes",
						"Supersets",
						"Strength EMOM",
					],
				},
				{
					t: "Conditioning",
					items: ["EMOM", "AMRAP", "For Time", "HIIT"],
				},
				{
					t: "Endurance",
					items: ["Running", "Bike", "Swimming", "Erg (ergometers)"],
				},
				{
					t: "Benchmark",
					items: [
						"Reference tests",
						"Group leaderboards",
						"Competition calendar",
					],
				},
			],
			foot: "Rounds, times and scores are logged by the athlete: the coach finds them in the history.",
		},
		ai: {
			kicker: "Kraveon and artificial intelligence",
			title: "An AI that works with your data",
			generic: {
				t: "Generic chatbot",
				items: [
					"Doesn't know your athletes",
					"You paste data by hand",
					"Returns text to copy into the program",
					"Starts from scratch every time",
				],
			},
			linked: {
				t: "AI connected to Kraveon",
				items: [
					"Reads athletes, results, feedback and competitions",
					"Uses the real exercises in your library",
					"Writes the program straight into the app",
					"Leaves an unpublished draft for you to approve",
				],
			},
			demo: {
				t: "The AI prepares. The coach decides.",
				coach: "Coach",
				ask: "“Add a deload week after week 4 for the demo athlete and bring the squats to 65%.”",
				reply: "AI + Kraveon",
				answer: "Week 5 created as a draft, not yet published to athletes. Restore point saved.",
			},
			foot: "Works with the built-in assistant and with the connector for Claude and ChatGPT.",
		},
		models: {
			kicker: "Performance models",
			title: "Each athlete's gaps, at a glance",
			points: [
				{
					t: "Sports and models",
					d: "Use the system model or build your own from scratch.",
				},
				{
					t: "Level out of 10 for each exercise",
					d: "With the kg missing to the next level.",
				},
				{
					t: "Radar per domain",
					d: "Strengths and areas to develop: before writing the next block you know what to work on.",
				},
			],
			card: {
				label: "Weightlifting · −85 kg",
				rows: [
					{ n: "Back squat", m: "160 kg · Competitive", lvl: 4, gap: "+27 kg" },
					{ n: "Snatch", m: "115 kg · National level", lvl: 5, gap: "+15 kg" },
					{ n: "Clean & jerk", m: "135 kg · Competitive", lvl: 4, gap: "+4 kg" },
				],
				levelOf: "Level",
				toNext: "to the next",
				note: "Level and kg to the next one according to the Weightlifting system model.",
			},
			foot: "Sporting assessment of performance: levels are an indicative reading of the maxes.",
		},
		weight: {
			kicker: "Weight classes",
			title: "−85 or −75? Choose with the numbers",
			from: "Class −85 kg",
			to: "Class −75 kg · Compare with",
			rows: [
				{ n: "Snatch · 110 kg", a: 4, b: 5 },
				{ n: "Clean & jerk · 135 kg", a: 4, b: 5 },
				{ n: "Back squat · 175 kg", a: 4, b: 5 },
			],
			qLabel: "The question",
			q: "Which class should the athlete compete in? It's often decided on gut feeling.",
			rLabel: "The answer",
			r: "With “Compare with” you read the same maxes on both tables, with the Sinclair value.",
			foot: "Sample data · thresholds of the Weightlifting system model.",
		},
		progression: {
			kicker: "Progressions",
			title: "Progression at a glance",
			label: "Mesocycle · 4 weeks · Demo athlete",
			exercise: "Exercise",
			week: "Week",
			rows: [
				["Back Squat", "4 × 5 @ 70%", "4 × 5 @ 75%", "5 × 5 @ 75%", "5 × 5 @ 80%"],
				["Front Squat", "3 × 5 @ 65%", "3 × 5 @ 70%", "4 × 5 @ 70%", "4 × 5 @ 75%"],
				["Snatch", "5 × 2 @ 70%", "6 × 2 @ 70%", "6 × 2 @ 75%", "7 × 2 @ 75%"],
				["Deadlift", "3 × 5 @ 70%", "4 × 5 @ 75%", "4 × 5 @ 75%", "5 × 5 @ 80%"],
			],
			volume: "Weekly volume",
			volumeSub: "Total reps",
			volumes: [60, 67, 77, 84],
			foot: "Loads as a percentage of the max: the app calculates each athlete's kg and tracks the trend over time.",
		},
		audience: {
			kicker: "Who it's for",
			title: "From the single athlete to the box with several coaches",
			items: [
				{
					t: "Personal trainers",
					items: [
						"Individual programs",
						"Loads based on the max",
						"Athlete diary and notes",
					],
				},
				{
					t: "Online coaches",
					items: [
						"Athletes train anywhere from the web app",
						"Feedback in the context of the session",
					],
				},
				{
					t: "Cross & functional training",
					items: [
						"Native EMOM, AMRAP, For Time and HIIT",
						"Benchmarks and group leaderboards",
						"Competition calendar",
					],
				},
				{
					t: "Boxes and gyms",
					items: [
						"Programming for groups",
						"One athlete in several groups",
						"Community with leaderboards",
					],
				},
				{
					t: "Multi-coach staff",
					items: [
						"Several coaches on the same athlete",
						"Each coach sees their own athletes",
						"Data kept separate between coaches",
					],
				},
			],
			pills: [
				"Installable web app",
				"Italian and English",
				"Light and dark theme",
				"Athletes by coach invitation",
			],
		},
		final: {
			lines: [
				"You decide the program.",
				"Kraveon organises it.",
				"AI helps you prepare it.",
			],
			tagline: "By coaches, for coaches.",
			cta: "Discover Kraveon",
		},
		footer: { privacy: "Privacy", cookie: "Cookies" },
	},
};

const WHY_ICONS = [Sparkles, BarChart3, Scale, CalendarCheck, Layers, Users];
const TRAINING_ICONS = [Dumbbell, Timer, Activity, Trophy];

export default function Info() {
	const { language } = useLanguage();
	const navigate = useNavigate();
	const c = COPY[language === "en" ? "en" : "it"];

	useLandingDocumentScroll();
	useMetricoolTracker();
	useRevealOnScroll(true, language);

	const goLogin = () => navigate("/login");

	return (
		<div className="theme-dark-scope min-h-screen bg-obsidian text-white font-body antialiased selection:bg-volt/30">
			{/* NAV */}
			<header className="sticky top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-xl">
				<div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
					<Link to="/" className="flex items-center gap-2.5 shrink-0">
						<img src="/logo-kraveon.svg" alt="Kraveon" className="h-8 w-8" />
						<span className="font-heading font-extrabold uppercase tracking-tight text-xl">
							Kraveon
						</span>
					</Link>
					<div className="flex items-center gap-2 sm:gap-3">
						<LanguageToggle />
						<Button
							variant="ghost"
							onClick={goLogin}
							className="h-11 text-white hover:bg-white/5 rounded-sm"
						>
							{c.login}
						</Button>
					</div>
				</div>
			</header>

			{/* HERO */}
			<section className="relative overflow-hidden">
				<div className="absolute inset-0 krv-grid-bg pointer-events-none" />
				<div
					className="absolute inset-0 pointer-events-none"
					style={{
						background:
							"radial-gradient(ellipse 70% 50% at 80% 30%, rgba(0,214,143,0.16) 0%, rgba(0,0,0,0) 70%)",
					}}
				/>
				<div className="relative max-w-6xl mx-auto px-5 pt-14 pb-16 md:pt-24 md:pb-24 grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
					<div>
						<span className="krv-kicker krv-kicker--wrap">{c.hero.kicker}</span>
						<h1 className="mt-6 font-heading font-extrabold uppercase tracking-tight text-[2.75rem] leading-[0.95] sm:text-6xl md:text-7xl">
							{c.hero.h1a}
							<br />
							<span className="text-volt">{c.hero.h1b}</span>
						</h1>
						<p className="mt-6 text-lg md:text-xl text-white/70 leading-relaxed max-w-xl">
							{c.hero.sub}
						</p>
						<div className="mt-8 flex flex-col sm:flex-row gap-3">
							<Button
								asChild
								className="h-12 px-7 bg-volt text-black font-bold uppercase tracking-wider rounded-sm hover:bg-volt/90 text-base"
							>
								<Link to="/">
									{c.hero.cta2} <ArrowRight size={18} />
								</Link>
							</Button>
							<Button
								variant="outline"
								onClick={goLogin}
								className="h-12 px-7 rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 text-base"
							>
								{c.app}
							</Button>
						</div>
					</div>
					<MesocycleCard m={c.hero.meso} example={c.example} />
				</div>
			</section>

			{/* PROBLEMA */}
			<section className="border-y border-white/10 bg-charcoal/50">
				<div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
					<SectionHeader
						label={c.problem.kicker}
						title={c.problem.title}
						align="left"
					/>
					<div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3" data-reveal>
						{c.problem.items.map((p) => (
							<div
								key={p.k}
								className="rounded-sm border border-white/10 bg-card p-6"
							>
								<div className="krv-kicker">{p.k}</div>
								<p className="mt-3 text-white/85 leading-relaxed">{p.v}</p>
							</div>
						))}
					</div>
					<p
						className="mt-8 font-heading font-extrabold uppercase tracking-tight text-2xl md:text-3xl leading-tight text-acid"
						data-reveal
					>
						{c.problem.chaos}
					</p>
				</div>
			</section>

			{/* SOLUZIONE */}
			<section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
				<SectionHeader
					label={c.solution.kicker}
					title={c.solution.title}
					align="left"
				/>
				<ol className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3" data-reveal>
					{c.solution.steps.map((s, i) => (
						<li
							key={s.t}
							className={`flex flex-col rounded-sm border bg-card p-5 ${
								s.ai ? "border-volt/50" : "border-white/10"
							}`}
						>
							<span className="font-mono text-xs text-volt">
								{String(i + 1).padStart(2, "0")}
							</span>
							<h3 className="mt-3 font-heading font-bold uppercase tracking-tight text-2xl leading-none">
								{s.t}
							</h3>
							<p className="mt-3 text-sm text-white/65 leading-relaxed flex-1">
								{s.d}
							</p>
							<span
								className={`mt-4 font-mono text-xs uppercase tracking-[0.12em] ${
									s.ai ? "text-volt" : "text-white/80"
								}`}
							>
								{s.who}
							</span>
						</li>
					))}
				</ol>
			</section>

			{/* PERCHE' */}
			<section className="border-y border-white/10 bg-charcoal/50">
				<div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
					<SectionHeader label={c.why.kicker} title={c.why.title} align="left" />
					<div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-reveal>
						{c.why.items.map((w, i) => {
							const Icon = WHY_ICONS[i];
							return (
								<div
									key={w.t}
									className={`rounded-sm border bg-card p-6 ${
										w.hl ? "border-volt/50" : "border-white/10"
									}`}
								>
									<div className="flex items-center justify-between">
										<span className="font-mono text-xs text-volt">
											{String(i + 1).padStart(2, "0")}
										</span>
										<Icon className="h-5 w-5 text-white/40" strokeWidth={1.75} />
									</div>
									<h3 className="mt-3 font-heading font-bold uppercase tracking-tight text-xl leading-tight">
										{w.t}
									</h3>
									<p className="mt-2 text-sm text-white/65 leading-relaxed">{w.d}</p>
								</div>
							);
						})}
					</div>
				</div>
			</section>

			{/* PROGRAMMAZIONE */}
			<section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
				<div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-start">
					<div>
						<SectionHeader
							label={c.builder.kicker}
							title={c.builder.title}
							align="left"
						/>
						<ul className="mt-8 space-y-5" data-reveal>
							{c.builder.points.map((p) => (
								<li key={p.t} className="border-l-2 border-volt pl-4">
									<p className="font-semibold text-white">{p.t}</p>
									<p className="mt-1 text-sm text-white/60">{p.d}</p>
								</li>
							))}
						</ul>
					</div>
					<AthleteSessionCard p={c.builder.phone} example={c.example} />
				</div>
			</section>

			{/* ALLENAMENTI */}
			<section className="border-y border-white/10 bg-charcoal/50">
				<div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
					<SectionHeader
						label={c.training.kicker}
						title={c.training.title}
						align="left"
					/>
					<div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-reveal>
						{c.training.groups.map((g, i) => {
							const Icon = TRAINING_ICONS[i];
							return (
								<div
									key={g.t}
									className="rounded-sm border border-white/10 bg-card p-6"
								>
									<div className="flex items-center gap-2.5">
										<Icon className="h-5 w-5 text-volt" strokeWidth={1.75} />
										<h3 className="font-heading font-extrabold uppercase tracking-tight text-2xl leading-none text-volt">
											{g.t}
										</h3>
									</div>
									<ul className="mt-4 space-y-2 text-white/85">
										{g.items.map((item) => (
											<li key={item} className="flex items-start gap-2">
												<Check
													size={14}
													className="text-volt shrink-0 mt-[0.3em]"
													strokeWidth={2.5}
												/>
												{item}
											</li>
										))}
									</ul>
								</div>
							);
						})}
					</div>
					<p className="mt-8 text-white/60" data-reveal>
						{c.training.foot}
					</p>
				</div>
			</section>

			{/* AI */}
			<section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
				<SectionHeader label={c.ai.kicker} title={c.ai.title} align="left" />
				<div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3" data-reveal>
					<div className="rounded-sm border border-white/10 bg-card p-6">
						<div className="krv-kicker !text-white/55">{c.ai.generic.t}</div>
						<ul className="mt-4 space-y-3 text-white/60">
							{c.ai.generic.items.map((item) => (
								<li key={item} className="flex items-start gap-2.5">
									<Minus size={16} className="shrink-0 mt-[0.2em]" />
									{item}
								</li>
							))}
						</ul>
					</div>
					<div className="rounded-sm border border-volt/50 bg-card p-6">
						<div className="krv-kicker">{c.ai.linked.t}</div>
						<ul className="mt-4 space-y-3 text-white/90">
							{c.ai.linked.items.map((item) => (
								<li key={item} className="flex items-start gap-2.5">
									<Plus size={16} className="shrink-0 mt-[0.2em] text-volt" />
									{item}
								</li>
							))}
						</ul>
					</div>
				</div>
				<div className="mt-3 rounded-sm border border-white/10 bg-card p-5 md:p-6" data-reveal>
					<div className="flex items-center justify-between gap-3">
						<span className="krv-kicker krv-kicker--wrap !text-acid">
							{c.ai.demo.t}
						</span>
						<ExampleTag label={c.example} />
					</div>
					<div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
						<div className="rounded-sm bg-white/[0.07] p-4">
							<div className="font-mono text-xs uppercase tracking-[0.12em] text-white/55">
								{c.ai.demo.coach}
							</div>
							<p className="mt-2 text-white/90 leading-relaxed">{c.ai.demo.ask}</p>
						</div>
						<div className="rounded-sm border border-volt/50 bg-obsidian p-4">
							<div className="font-mono text-xs uppercase tracking-[0.12em] text-volt">
								{c.ai.demo.reply}
							</div>
							<p className="mt-2 text-white/90 leading-relaxed">{c.ai.demo.answer}</p>
						</div>
					</div>
				</div>
				<p className="mt-6 text-sm text-white/55" data-reveal>
					{c.ai.foot}
				</p>
			</section>

			{/* MODELLI PRESTATIVI */}
			<section className="border-y border-white/10 bg-charcoal/50">
				<div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
					<div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-start">
						<div>
							<SectionHeader
								label={c.models.kicker}
								title={c.models.title}
								align="left"
							/>
							<ul className="mt-8 space-y-5" data-reveal>
								{c.models.points.map((p) => (
									<li key={p.t}>
										<p className="font-semibold text-white">{p.t}</p>
										<p className="mt-1 text-white/60 leading-relaxed">{p.d}</p>
									</li>
								))}
							</ul>
						</div>
						<LevelsCard m={c.models.card} example={c.exampleData} />
					</div>
					<p className="mt-8 text-sm text-white/55" data-reveal>
						{c.models.foot}
					</p>
				</div>
			</section>

			{/* CATEGORIE DI PESO */}
			<section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
				<SectionHeader label={c.weight.kicker} title={c.weight.title} align="left" />
				<div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3" data-reveal>
					<WeightClassCard title={c.weight.from} rows={c.weight.rows} field="a" />
					<WeightClassCard
						title={c.weight.to}
						rows={c.weight.rows}
						field="b"
						highlight
					/>
				</div>
				<div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-reveal>
					<div>
						<div className="krv-kicker !text-acid">{c.weight.qLabel}</div>
						<p className="mt-3 text-white/85 leading-relaxed">{c.weight.q}</p>
					</div>
					<div>
						<div className="krv-kicker">{c.weight.rLabel}</div>
						<p className="mt-3 text-white/85 leading-relaxed">{c.weight.r}</p>
					</div>
				</div>
				<p className="mt-8 text-sm text-white/55">{c.weight.foot}</p>
			</section>

			{/* PROGRESSIONI */}
			<section className="border-y border-white/10 bg-charcoal/50">
				<div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
					<SectionHeader
						label={c.progression.kicker}
						title={c.progression.title}
						align="left"
					/>
					<ProgressionCard p={c.progression} example={c.exampleData} />
					<p className="mt-6 text-sm text-white/55">{c.progression.foot}</p>
				</div>
			</section>

			{/* PER CHI E' */}
			<section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
				<SectionHeader
					label={c.audience.kicker}
					title={c.audience.title}
					align="left"
				/>
				<div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-reveal>
					{c.audience.items.map((a) => (
						<div key={a.t} className="rounded-sm border border-white/10 bg-card p-5">
							<h3 className="font-heading font-extrabold uppercase tracking-tight text-xl leading-tight text-volt">
								{a.t}
							</h3>
							<ul className="mt-4 space-y-2 text-sm text-white/85">
								{a.items.map((item) => (
									<li key={item} className="flex items-start gap-2">
										<Check
											size={14}
											className="text-volt shrink-0 mt-[0.25em]"
											strokeWidth={2.5}
										/>
										{item}
									</li>
								))}
							</ul>
						</div>
					))}
				</div>
				<ul className="mt-6 flex flex-wrap gap-2" data-reveal>
					{c.audience.pills.map((pill) => (
						<li key={pill} className="krv-pill">
							{pill}
						</li>
					))}
				</ul>
			</section>

			{/* CHIUSURA */}
			<section className="bg-volt text-black">
				<div className="max-w-6xl mx-auto px-5 py-20 md:py-28">
					<img src="/logo-kraveon.svg" alt="" className="h-12 w-12" />
					<p className="mt-8 font-heading font-extrabold uppercase tracking-tight text-4xl sm:text-5xl md:text-7xl leading-[0.98]">
						{c.final.lines.map((line) => (
							<span key={line} className="block">
								{line}
							</span>
						))}
					</p>
					<p className="mt-8 text-lg uppercase tracking-wide">{c.final.tagline}</p>
					<div className="mt-8 flex flex-col sm:flex-row gap-3">
						<Button
							asChild
							className="h-12 px-7 bg-black text-white font-bold uppercase tracking-wider rounded-sm hover:bg-black/85 text-base"
						>
							<Link to="/">
								{c.final.cta} <ArrowRight size={18} />
							</Link>
						</Button>
						<Button
							variant="outline"
							onClick={goLogin}
							className="h-12 px-7 rounded-sm border-black/40 bg-transparent text-black hover:bg-black/10 text-base"
						>
							{c.app}
						</Button>
					</div>
				</div>
			</section>

			{/* FOOTER */}
			<footer className="border-t border-white/10">
				<div className="max-w-6xl mx-auto px-5 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
					<Link to="/" className="flex items-center gap-2.5">
						<img src="/logo-kraveon.svg" alt="Kraveon" className="h-7 w-7" />
						<span className="font-heading font-extrabold uppercase tracking-tight">
							Kraveon
						</span>
					</Link>
					<div className="flex items-center gap-6 text-sm text-white/60">
						<Link to="/privacy-policy" className="hover:text-white">
							{c.footer.privacy}
						</Link>
						<Link to="/cookie-policy" className="hover:text-white">
							{c.footer.cookie}
						</Link>
						<button onClick={goLogin} className="hover:text-white">
							{c.login}
						</button>
					</div>
					<p className="text-xs text-white/45 font-mono">
						© {new Date().getFullYear()} Kraveon
					</p>
				</div>
			</footer>
		</div>
	);
}

function ExampleTag({ label }) {
	return (
		<span className="krv-tag shrink-0 border border-white/15 bg-white/[0.06] text-white/75">
			{label}
		</span>
	);
}

function MesocycleCard({ m, example }) {
	return (
		<div className="rounded-sm border border-white/10 bg-card p-5 md:p-6" data-reveal>
			<div className="flex items-center justify-between gap-3">
				<span className="font-mono text-xs uppercase tracking-[0.12em] text-white/55">
					{m.label}
				</span>
				<ExampleTag label={example} />
			</div>
			<p className="mt-3 font-heading font-extrabold uppercase tracking-tight text-2xl">
				{m.title}
			</p>
			<table className="mt-4 w-full text-sm border-collapse">
				<thead>
					<tr className="text-white/55 text-left">
						<th className="py-2 pr-2 font-medium">{m.exercise}</th>
						{m.weeks.map((w) => (
							<th key={w} className="py-2 px-1 font-medium text-right">
								{w}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{m.rows.map(([name, ...values]) => (
						<tr key={name} className="border-t border-white/10">
							<td className="py-2.5 pr-2 text-white/90">{name}</td>
							{values.map((v, i) => (
								<td
									key={i}
									className={`py-2.5 px-1 text-right font-mono tabular-nums ${
										i === values.length - 1 ? "text-volt" : "text-white/85"
									}`}
								>
									{v}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
			<p className="mt-3 text-xs text-white/55">{m.note}</p>
		</div>
	);
}

function AthleteSessionCard({ p, example }) {
	return (
		<div className="rounded-sm border border-white/10 bg-card p-5 md:p-6" data-reveal>
			<div className="flex items-center justify-between gap-3">
				<span className="font-mono text-xs uppercase tracking-[0.12em] text-white/55">
					{p.label}
				</span>
				<ExampleTag label={example} />
			</div>
			<p className="mt-3 font-heading font-extrabold uppercase tracking-tight text-3xl">
				{p.title}
			</p>
			<div className="mt-4 space-y-3">
				{p.items.map((item) => (
					<div
						key={item.n}
						className="rounded-sm border border-white/10 bg-obsidian p-4"
					>
						<p className="font-semibold text-white">{item.n}</p>
						<p className="mt-1 font-mono text-volt">{item.p}</p>
						<p className="mt-1 text-xs text-white/55">{p.loadNote}</p>
					</div>
				))}
			</div>
			<p className="mt-4 text-sm text-white/60">{p.foot}</p>
		</div>
	);
}

function LevelsCard({ m, example }) {
	return (
		<div className="rounded-sm border border-white/10 bg-card p-5 md:p-6" data-reveal>
			<div className="flex items-center justify-between gap-3">
				<span className="font-mono text-xs uppercase tracking-[0.12em] text-white/55">
					{m.label}
				</span>
				<ExampleTag label={example} />
			</div>
			<ul className="mt-4 space-y-5">
				{m.rows.map((r) => (
					<li key={r.n}>
						<div className="flex items-baseline justify-between gap-3">
							<p className="font-semibold text-white">{r.n}</p>
							<p className="font-mono text-sm tabular-nums">
								<span className="text-white/85">
									{m.levelOf} {r.lvl}/10
								</span>
								<span className="ml-3 text-acid">
									{r.gap} <span className="sr-only">{m.toNext}</span>
								</span>
							</p>
						</div>
						<p className="mt-0.5 text-xs text-white/55">{r.m}</p>
						<div
							className="mt-2 h-2 rounded-sm bg-white/10"
							role="img"
							aria-label={`${m.levelOf} ${r.lvl}/10`}
						>
							<div
								className="h-full rounded-sm bg-volt"
								style={{ width: `${r.lvl * 10}%` }}
							/>
						</div>
					</li>
				))}
			</ul>
			<p className="mt-5 text-xs text-white/55">{m.note}</p>
		</div>
	);
}

function WeightClassCard({ title, rows, field, highlight = false }) {
	return (
		<div
			className={`rounded-sm border bg-card p-6 ${
				highlight ? "border-volt/50" : "border-white/10"
			}`}
		>
			<div className={`krv-kicker krv-kicker--wrap ${highlight ? "" : "!text-white/55"}`}>
				{title}
			</div>
			<ul className="mt-4 space-y-3">
				{rows.map((r) => (
					<li key={r.n} className="flex items-center justify-between gap-3">
						<span className="text-white/90">{r.n}</span>
						<span
							className={`font-mono tabular-nums ${
								highlight ? "text-volt" : "text-white/85"
							}`}
						>
							{r[field]}/10
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}

function ProgressionCard({ p, example }) {
	const maxVolume = Math.max(...p.volumes);
	return (
		<div className="mt-10 rounded-sm border border-white/10 bg-card p-5 md:p-6" data-reveal>
			<div className="flex items-center justify-between gap-3">
				<span className="font-mono text-xs uppercase tracking-[0.12em] text-white/55">
					{p.label}
				</span>
				<ExampleTag label={example} />
			</div>
			{/* Su telefono la matrice scorre dentro la scheda, mai la pagina. */}
			<div className="mt-4 -mx-5 md:mx-0 px-5 md:px-0 overflow-x-auto">
				<table className="w-full min-w-[640px] text-sm border-collapse">
					<thead>
						<tr className="text-left text-white/55">
							<th className="py-2 pr-3 font-medium">{p.exercise}</th>
							{p.volumes.map((_, i) => (
								<th key={i} className="py-2 px-2 font-medium">
									{p.week} {i + 1}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{p.rows.map(([name, ...weeks]) => (
							<tr key={name} className="border-t border-white/10">
								<td className="py-3 pr-3 font-semibold text-white">{name}</td>
								{weeks.map((w, i) => (
									<td
										key={i}
										className={`py-3 px-2 font-mono tabular-nums whitespace-nowrap ${
											i > 0 && w !== weeks[i - 1] ? "text-volt" : "text-white/85"
										}`}
									>
										{w}
									</td>
								))}
							</tr>
						))}
						<tr className="border-t border-white/10">
							<td className="pt-4 pr-3 align-bottom">
								<p className="font-semibold text-acid">{p.volume}</p>
								<p className="text-xs text-white/55">{p.volumeSub}</p>
							</td>
							{p.volumes.map((v, i) => (
								<td key={i} className="pt-4 px-2 align-bottom">
									<p
										className={`font-mono tabular-nums ${
											i === p.volumes.length - 1 ? "text-acid" : "text-white/85"
										}`}
									>
										{v}
									</p>
									<div
										className={`mt-1 w-full max-w-[7rem] rounded-sm ${
											i === p.volumes.length - 1 ? "bg-acid" : "bg-volt"
										}`}
										style={{ height: `${Math.round((v / maxVolume) * 56)}px` }}
										aria-hidden="true"
									/>
								</td>
							))}
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
}
